import { FLAG_STAGE_FILTER_LABELS, isFlagFilterColumn } from '@/lib/tableFilters/projectFilterMigrations';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/Button/Button';
import { FilterPopoverShell } from './filter-popover/FilterPopoverShell';
import { EdgeToEdgeTabs } from '@/components/ui/EdgeToEdgeTabs/EdgeToEdgeTabs';
import { SelectDropdown } from '@/components/ui/SelectDropdown/SelectDropdown';
import { CloseIcon, SearchIcon } from '@/components/ui/Icons';
import { Switch } from '@/components/ui/Switch/Switch';
import type {
  FilterRule,
  FilterColumnType,
  TextFilterOp,
  NumberFilterOp,
  DateFilterOp,
} from '@/lib/tableFilters';
import { isRuleEmpty } from '@/lib/tableFilters/filterRuleEmpty';
import type { Project } from '@/api/client';
import { contractorDisplayName } from '@/utils/contractorDisplayName';
import './EditableCell.css';
import './FilterPopover.css';

// ─── Labels ────────────────────────────────────────────────────────────────────

const TEXT_OP_LABELS: Record<TextFilterOp, string> = {
  none: 'Не выбран',
  contains: 'Содержит',
  notContains: 'Не содержит',
  equals: 'Равно',
  startsWith: 'Начинается с',
  isEmpty: 'Пусто',
  isNotEmpty: 'Не пусто',
};

const NUMBER_OP_LABELS: Record<NumberFilterOp, string> = {
  none: 'Не выбран',
  eq: '= равно',
  neq: '≠ не равно',
  gt: '> больше',
  lt: '< меньше',
  gte: '≥ больше или равно',
  lte: '≤ меньше или равно',
  between: 'Диапазон',
  isEmpty: 'Пусто',
  isNotEmpty: 'Не пусто',
};

const DATE_OP_LABELS: Record<DateFilterOp, string> = {
  none: 'Не выбран',
  equals: 'Равно',
  before: 'До',
  after: 'После',
  between: 'Диапазон',
  isEmpty: 'Пусто',
  isNotEmpty: 'Не пусто',
};

export const COLUMN_TITLES: Record<string, string> = {
  name: 'Название',
  author: 'Автор',
  theme: 'Тематика',
  rights: 'Права',
  asta: 'ASTA',
  ase: 'ASE',
  rin: 'РИН',
  paper_book_plan: 'План бумаги',
  producer: 'Продюсер',
  techeditor: 'Техредактор',
  corrector: 'Корректор',
  editor: 'Редактор',
  edition: 'Редакция',
  department: 'Подразделение',
  state: 'Состояние',
  project_type: 'Тип',
  review_check_type: 'Тип проверки',
  project_phonogram: 'Фонограмма',
  project_pdf: 'PDF',
  has_music: 'Фонотека',
  readers: 'Чтец',
  studio: 'Контрагенты',
  deadline: 'Дедлайн',
  review_deadline: 'Дедлайн проверки',
  pages: 'Страницы',
  author_sheets: 'АЛ',
  recording_coef: 'КФ записи',
  montage_coef: 'КФ монтажа',
  project_dp: 'ЦП',
  project_approved: 'Согласовано',
  project_done: 'Проект сдан',
  no_edits: 'Без правок',
};

// ─── Option extractors ─────────────────────────────────────────────────────────

function getTextOptions(columnId: string, projects: Project[]): string[] {
  const set = new Set<string>();
  for (const p of projects) {
    let val: string | null | undefined;
    switch (columnId) {
      case 'name':          val = p.name; break;
      case 'author':        val = p.authors?.[0]?.name ?? null; break;
      case 'theme':         val = p.theme; break;
      case 'rights':        val = p.rights; break;
      case 'asta':          val = p.asta; break;
      case 'ase':           val = p.ase; break;
      case 'rin':           val = p.rin; break;
      case 'paper_book_plan': val = p.paper_book_plan; break;
      case 'producer':      val = p.producers?.[0]?.name ?? null; break;
      case 'techeditor':    val = p.techeditors?.[0]?.name ?? null; break;
      case 'corrector':     val = p.correctors?.[0]?.name ?? null; break;
      case 'editor':        val = p.editor_name; break;
      case 'edition':       val = p.edition_name; break;
      case 'department':    val = p.edition_department_code; break;
    }
    if (val != null && val !== '') set.add(val);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'));
}

function getSelectOptions(columnId: string, projects: Project[]): string[] {
  // Отметки — все четыре стадии по порядку, а не только встреченные
  if (isFlagFilterColumn(columnId)) return [...FLAG_STAGE_FILTER_LABELS];
  const set = new Set<string>();
  for (const p of projects) {
    let val: string | null | undefined;
    switch (columnId) {
      case 'state':             val = p.state; break;
      case 'project_type':      val = p.project_type as string | undefined; break;
      case 'review_check_type': val = p.review_check_type as string | undefined; break;
    }
    if (val != null && val !== '') set.add(val);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'));
}

function getPersonOptions(columnId: string, projects: Project[]): string[] {
  const set = new Set<string>();
  for (const p of projects) {
    if (columnId === 'readers') {
      p.readers.forEach((r) => r.name && set.add(r.name));
    } else if (columnId === 'studio') {
      (p.contractors ?? []).forEach((c) => {
        const d = contractorDisplayName(c);
        if (d) set.add(d);
      });
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'));
}

export function getOptionsForColumn(
  columnId: string,
  filterType: FilterColumnType,
  projects: Project[]
): string[] {
  switch (filterType) {
    case 'text':        return getTextOptions(columnId, projects);
    case 'select':      return getSelectOptions(columnId, projects);
    case 'multiPerson': return getPersonOptions(columnId, projects);
    default:            return [];
  }
}

// ─── Default rules factory ─────────────────────────────────────────────────────

export function createDefaultRule(filterType: FilterColumnType): FilterRule {
  switch (filterType) {
    case 'text':        return { type: 'text', op: 'none', value: '', selectedValues: [] };
    case 'select':      return { type: 'select', values: [] };
    case 'multiPerson': return { type: 'multiPerson', names: [], op: 'none', value: '' };
    case 'date':        return { type: 'date', op: 'none', value: '' };
    case 'number':      return { type: 'number', op: 'none', value: 0 };
    case 'boolean':     return { type: 'boolean', value: null };
  }
}

// ─── Reusable checklist ────────────────────────────────────────────────────────

export interface FilterOptionsChecklistProps {
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  initialSearch?: string;
  renderOptionLabel?: (option: string) => React.ReactNode;
  /** Для фильтра списка (поиск работает по этой строке). */
  searchTextOf?: (option: string) => string;
}

export function FilterOptionsChecklist({
  options,
  selected,
  onChange,
  initialSearch = '',
  renderOptionLabel,
  searchTextOf,
}: FilterOptionsChecklistProps) {
  const [search, setSearch] = useState(initialSearch);

  const filtered = useMemo(
    () =>
      options.filter((o) => {
        const hay = (searchTextOf?.(o) ?? o).toLowerCase();
        return hay.includes(search.toLowerCase());
      }),
    [options, search, searchTextOf]
  );

  const allSelected = filtered.length > 0 && filtered.every((o) => selected.includes(o));
  const someSelected = filtered.some((o) => selected.includes(o));

  const toggleOption = useCallback(
    (opt: string) => {
      onChange(
        selected.includes(opt)
          ? selected.filter((v) => v !== opt)
          : [...selected, opt]
      );
    },
    [selected, onChange]
  );

  const toggleAll = useCallback(() => {
    if (allSelected) {
      onChange(selected.filter((v) => !filtered.includes(v)));
    } else {
      const added = filtered.filter((o) => !selected.includes(o));
      onChange([...selected, ...added]);
    }
  }, [allSelected, filtered, selected, onChange]);

  return (
    <div className="filter-popover__checklist-stack">
      <div className="filter-popover__search-wrap">
        <SearchIcon size={14} className="filter-popover__search-icon" />
        <input
          type="text"
          className="filter-popover__search form__input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по значениям..."
        />
      </div>

      <div className="filter-popover__checklist">
        {filtered.length === 0 ? (
          <div className="filter-popover__empty">Нет совпадений</div>
        ) : (
          <>
            <Switch
              className="filter-popover__check-item filter-popover__check-item_all"
              checked={allSelected}
              indeterminate={!allSelected && someSelected}
              preventFocusScroll
              onCheckedChange={() => toggleAll()}
              label={
                <span className="filter-popover__select-all-label">
                  Выбрать все
                  <span className="filter-popover__count-badge">{filtered.length}</span>
                </span>
              }
            />
            <div className="filter-popover__check-divider" />
            {filtered.map((opt) => (
              <Switch
                key={opt}
                className="filter-popover__check-item"
                checked={selected.includes(opt)}
                preventFocusScroll
                onCheckedChange={() => toggleOption(opt)}
                label={<span className="filter-popover__check-label">{renderOptionLabel ? renderOptionLabel(opt) : opt}</span>}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Per-type filter sections ──────────────────────────────────────────────────

interface FilterSectionProps {
  rule: FilterRule;
  columnId: string;
  filterType: FilterColumnType;
  allOptions: string[];
  onChange: (rule: FilterRule) => void;
  autoFocusInput?: boolean;
}

const TEXT_OP_OPTIONS = Object.entries(TEXT_OP_LABELS).map(([value, label]) => ({ value, label }));
const NUMBER_OP_OPTIONS = Object.entries(NUMBER_OP_LABELS).map(([value, label]) => ({ value, label }));
const DATE_OP_OPTIONS = Object.entries(DATE_OP_LABELS).map(([value, label]) => ({ value, label }));


export function FilterSection({
  rule,
  columnId: _columnId,
  filterType,
  allOptions,
  onChange,
  autoFocusInput,
}: FilterSectionProps) {
  if (filterType === 'text') {
    const r = rule as Extract<FilterRule, { type: 'text' }>;
    const criteriaActive = r.op !== 'none';
    const needsValue = criteriaActive && r.op !== 'isEmpty' && r.op !== 'isNotEmpty';

    const handleOpChange = (v: string) => {
      const op = v as TextFilterOp;
      if (op === 'none') {
        // Switch to values mode: clear criteria value
        onChange({ ...r, op, value: '' });
      } else {
        // Switch to criteria mode: clear selected values
        onChange({ ...r, op, selectedValues: [] });
      }
    };

    return (
      <div className="filter-popover__section-body">
        <div className="filter-popover__section-label">Критерий</div>
        <div className="filter-popover__criteria-row">
          <SelectDropdown
            options={TEXT_OP_OPTIONS}
            value={r.op}
            onChange={handleOpChange}
            className="filter-popover__criteria-select"
            dropdownListClassName="filter-popover__criteria-dropdown-list"
            scrollHighlightIntoView={false}
          />
          {needsValue && (
            <input
              type="text"
              className="form__input filter-popover__criteria-input"
              value={r.value}
              onChange={(e) => onChange({ ...r, value: e.target.value })}
              placeholder="Значение..."
              autoFocus={autoFocusInput}
            />
          )}
        </div>

        {!criteriaActive && allOptions.length > 0 && (
          <>
            <div className="filter-popover__section-divider" />
            <div className="filter-popover__section-label">Значения</div>
            <FilterOptionsChecklist
              options={allOptions}
              selected={r.selectedValues}
              onChange={(next) => onChange({ ...r, selectedValues: next })}
            />
          </>
        )}
      </div>
    );
  }

  if (filterType === 'number') {
    const r = rule as Extract<FilterRule, { type: 'number' }>;
    const needsValue = r.op !== 'none' && r.op !== 'isEmpty' && r.op !== 'isNotEmpty';
    const isBetween = r.op === 'between';
    return (
      <div className="filter-popover__section-body">
        <div className="filter-popover__section-label">Критерий</div>
        <div className="filter-popover__criteria-row">
          <SelectDropdown
            options={NUMBER_OP_OPTIONS}
            value={r.op}
            onChange={(v) => onChange({ ...r, op: v as NumberFilterOp })}
            className="filter-popover__criteria-select"
            dropdownListClassName="filter-popover__criteria-dropdown-list"
            fullWidth
            scrollHighlightIntoView={false}
          />
          {needsValue && (
            <input
              type="number"
              className="form__input"
              value={isNaN(r.value) ? '' : r.value}
              onChange={(e) => onChange({ ...r, value: parseFloat(e.target.value) || 0 })}
              placeholder={isBetween ? 'От...' : 'Значение...'}
              autoFocus={autoFocusInput}
            />
          )}
          {isBetween && (
            <input
              type="number"
              className="form__input"
              value={r.value2 == null || isNaN(r.value2) ? '' : r.value2}
              onChange={(e) => onChange({ ...r, value2: parseFloat(e.target.value) || 0 })}
              placeholder="До..."
            />
          )}
        </div>
      </div>
    );
  }

  if (filterType === 'date') {
    const r = rule as Extract<FilterRule, { type: 'date' }>;
    const needsValue = r.op !== 'none' && r.op !== 'isEmpty' && r.op !== 'isNotEmpty';
    const isBetween = r.op === 'between';
    return (
      <div className="filter-popover__section-body">
        <div className="filter-popover__section-label">Критерий</div>
        <div className="filter-popover__criteria-row">
          <SelectDropdown
            options={DATE_OP_OPTIONS}
            value={r.op}
            onChange={(v) => onChange({ ...r, op: v as DateFilterOp })}
            className="filter-popover__criteria-select"
            dropdownListClassName="filter-popover__criteria-dropdown-list"
            fullWidth
            scrollHighlightIntoView={false}
          />
          {needsValue && (
            <input
              type="date"
              className="form__input"
              value={r.value}
              onChange={(e) => onChange({ ...r, value: e.target.value })}
              autoFocus={autoFocusInput}
            />
          )}
          {isBetween && (
            <input
              type="date"
              className="form__input"
              value={r.value2 ?? ''}
              onChange={(e) => onChange({ ...r, value2: e.target.value })}
            />
          )}
        </div>
      </div>
    );
  }

  if (filterType === 'select') {
    const r = rule as Extract<FilterRule, { type: 'select' }>;
    return (
      <div className="filter-popover__section-body">
        <div className="filter-popover__section-label">Значения</div>
        <FilterOptionsChecklist
          options={allOptions}
          selected={r.values}
          onChange={(next) => onChange({ ...r, values: next })}
        />
      </div>
    );
  }

  if (filterType === 'multiPerson') {
    const r = rule as Extract<FilterRule, { type: 'multiPerson' }>;
    const op = r.op ?? 'none';
    const criteriaActive = op !== 'none';
    const needsValue = criteriaActive && op !== 'isEmpty' && op !== 'isNotEmpty';

    const handleOpChange = (v: string) => {
      const nextOp = v as TextFilterOp;
      if (nextOp === 'none') {
        onChange({ ...r, op: nextOp, value: '' });
      } else {
        onChange({ ...r, op: nextOp, names: [] });
      }
    };

    return (
      <div className="filter-popover__section-body">
        <div className="filter-popover__section-label">Критерий</div>
        <div className="filter-popover__criteria-row">
          <SelectDropdown
            options={TEXT_OP_OPTIONS}
            value={op}
            onChange={handleOpChange}
            className="filter-popover__criteria-select"
            dropdownListClassName="filter-popover__criteria-dropdown-list"
            scrollHighlightIntoView={false}
          />
          {needsValue && (
            <input
              type="text"
              className="form__input filter-popover__criteria-input"
              value={r.value ?? ''}
              onChange={(e) => onChange({ ...r, value: e.target.value })}
              placeholder="Значение..."
              autoFocus={autoFocusInput}
            />
          )}
        </div>

        {!criteriaActive && allOptions.length > 0 && (
          <>
            <div className="filter-popover__section-divider" />
            <div className="filter-popover__section-label">Значения</div>
            <FilterOptionsChecklist
              options={allOptions}
              selected={r.names}
              onChange={(next) => onChange({ ...r, names: next })}
            />
          </>
        )}
      </div>
    );
  }

  if (filterType === 'boolean') {
    const r = rule as Extract<FilterRule, { type: 'boolean' }>;
    return (
      <div className="filter-popover__section-body">
        <div className="filter-popover__section-label">Значение</div>
        <div className="filter-popover__bool-row">
          {([null, true, false] as const).map((val) => (
            <button
              key={String(val)}
              type="button"
              className={`filter-popover__bool-option${r.value === val ? ' filter-popover__bool-option_active' : ''}`}
              onClick={() => onChange({ ...r, value: val })}
            >
              {val === null ? 'Все' : val ? 'Да' : 'Нет'}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return null;
}

// ─── FilterPopover (single column) ─────────────────────────────────────────────

export interface FilterPopoverProps {
  columnId: string;
  filterType: FilterColumnType;
  currentRule: FilterRule | null;
  onApply: (columnId: string, rule: FilterRule | null) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  onClose: () => void;
  /** Опции чеклиста; при указании имеет приоритет над источником из проектов. */
  getOptions?: (columnId: string, filterType: FilterColumnType) => string[];
  /** Каталог проектов для списков значений. */
  allProjects?: Project[];
  /** Строка в шапке вместо COLUMN_TITLES[columnId]. */
  headerLabel?: string;
}

export function FilterPopover({
  columnId,
  filterType,
  currentRule,
  onApply,
  triggerRef,
  isOpen,
  onClose,
  getOptions,
  allProjects,
  headerLabel,
}: FilterPopoverProps) {
  const [draftRule, setDraftRule] = useState<FilterRule>(() =>
    currentRule ?? createDefaultRule(filterType)
  );

  useEffect(() => {
    if (isOpen) setDraftRule(currentRule ?? createDefaultRule(filterType));
  }, [isOpen, currentRule, filterType]);

  const projectList = allProjects ?? [];

  const allOptions = useMemo(
    () =>
      getOptions
        ? getOptions(columnId, filterType)
        : getOptionsForColumn(columnId, filterType, projectList),
    [columnId, filterType, projectList, getOptions]
  );

  const handleClear = useCallback(() => {
    setDraftRule(createDefaultRule(filterType));
  }, [filterType]);

  const multiPersonCriteria =
    filterType === 'multiPerson' &&
    (draftRule as Extract<FilterRule, { type: 'multiPerson' }>).op !== 'none';
  const isChecklistOnly =
    filterType === 'select' ||
    (filterType === 'multiPerson' &&
      (draftRule as Extract<FilterRule, { type: 'multiPerson' }>).op === 'none');
  const panelWidthPx =
    columnId === 'readers'
      ? 340
      : isChecklistOnly
        ? 320
        : filterType === 'text' || multiPersonCriteria
          ? 340
          : 300;

  return (
    <FilterPopoverShell
      isOpen={isOpen}
      onClose={onClose}
      triggerRef={triggerRef}
      positionDependencies={[columnId]}
      panelWidthPx={panelWidthPx}
      onPanelKeyDown={(e, requestClose) => {
        if (e.key === 'Enter' && filterType !== 'select') {
          e.preventDefault();
          onApply(columnId, draftRule);
          requestClose();
        } else if (e.key === 'Escape') {
          requestClose();
        }
      }}
    >
      {({ requestClose }) => (
        <>
          <div className="filter-popover__header">
            <span className="filter-popover__title">
              <span className="filter-popover__title-prefix">Фильтр:</span>
              {headerLabel ?? COLUMN_TITLES[columnId] ?? columnId}
            </span>
            <button
              type="button"
              className="filter-popover__close-btn"
              onClick={requestClose}
              aria-label="Закрыть"
            >
              <CloseIcon size={16} />
            </button>
          </div>

          <div className="filter-popover__scroll">
            <FilterSection
              rule={draftRule}
              columnId={columnId}
              filterType={filterType}
              allOptions={allOptions}
              onChange={setDraftRule}
              autoFocusInput
            />
          </div>

          <div className="filter-popover__footer editable-cell-popover__footer">
            <Button variant="secondary" size="s" fullWidth onClick={handleClear}>
              Очистить
            </Button>
            <Button
              variant="primary"
              size="s"
              fullWidth
              onClick={() => {
                onApply(columnId, draftRule);
                requestClose();
              }}
            >
              Применить
            </Button>
          </div>
        </>
      )}
    </FilterPopoverShell>
  );
}

// ─── ComboFilterPopover (multi-column) ─────────────────────────────────────────

export interface ComboFilterChild {
  columnId: string;
  filterType: FilterColumnType;
  currentRule: FilterRule | null;
}

export interface ComboFilterPopoverProps {
  comboId: string;
  comboTitle: string;
  children: ComboFilterChild[];
  onApply: (columnId: string, rule: FilterRule | null) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  onClose: () => void;
  getOptions?: (columnId: string, filterType: FilterColumnType) => string[];
  allProjects?: Project[];
}

export function ComboFilterPopover({
  comboId,
  comboTitle,
  children: filterChildren,
  onApply,
  triggerRef,
  isOpen,
  onClose,
  getOptions,
  allProjects,
}: ComboFilterPopoverProps) {
  // Draft state per child column
  const [drafts, setDrafts] = useState<Record<string, FilterRule>>(() => {
    const init: Record<string, FilterRule> = {};
    for (const c of filterChildren) {
      init[c.columnId] = c.currentRule ?? createDefaultRule(c.filterType);
    }
    return init;
  });

  const [activeTab, setActiveTab] = useState(filterChildren[0]?.columnId ?? '');

  useEffect(() => {
    if (isOpen) {
      const init: Record<string, FilterRule> = {};
      for (const c of filterChildren) {
        init[c.columnId] = c.currentRule ?? createDefaultRule(c.filterType);
      }
      setDrafts(init);
      setActiveTab(filterChildren[0]?.columnId ?? '');
    }
  }, [isOpen, filterChildren]);

  const handleClearAll = useCallback(() => {
    setDrafts((prev) => {
      const next = { ...prev };
      for (const c of filterChildren) {
        next[c.columnId] = createDefaultRule(c.filterType);
      }
      return next;
    });
  }, [filterChildren]);

  const comboTabItems = useMemo(
    () =>
      filterChildren.map((c) => {
        const draft = drafts[c.columnId] ?? createDefaultRule(c.filterType);
        const showTabDot = !isRuleEmpty(draft);
        return {
          id: c.columnId,
          label: COLUMN_TITLES[c.columnId] ?? c.columnId,
          adornment: showTabDot ? (
            <span className="filter-popover__combo-tab-dot" aria-hidden />
          ) : undefined,
          adornmentPlacement: 'start' as const,
        };
      }),
    [filterChildren, drafts]
  );

  if (filterChildren.length === 0) return null;

  const activeChild = filterChildren.find((c) => c.columnId === activeTab) ?? filterChildren[0]!;
  const projectList = allProjects ?? [];
  const allOptions = getOptions
    ? getOptions(activeChild.columnId, activeChild.filterType)
    : getOptionsForColumn(activeChild.columnId, activeChild.filterType, projectList);
  const activeDraft = drafts[activeChild.columnId] ?? createDefaultRule(activeChild.filterType);
  const comboPanelWidthPx = filterChildren.some((c) => c.columnId === 'readers') ? 400 : 340;

  return (
    <FilterPopoverShell
      isOpen={isOpen}
      onClose={onClose}
      triggerRef={triggerRef}
      positionDependencies={[comboId]}
      panelWidthPx={comboPanelWidthPx}
    >
      {({ requestClose }) => (
        <>
          <div className="filter-popover__header">
            <span className="filter-popover__title">
              <span className="filter-popover__title-prefix">Фильтр:</span>
              {comboTitle}
            </span>
            <button
              type="button"
              className="filter-popover__close-btn"
              onClick={requestClose}
              aria-label="Закрыть"
            >
              <CloseIcon size={16} />
            </button>
          </div>

          <div className="filter-popover__body">
            {filterChildren.length > 1 ? (
              <div className="filter-popover__tabs">
                <EdgeToEdgeTabs
                  items={comboTabItems}
                  value={activeTab}
                  onChange={setActiveTab}
                  aria-label="Колонки комбо-фильтра"
                />
              </div>
            ) : null}

            <div className="filter-popover__scroll">
              <FilterSection
                key={activeChild.columnId}
                rule={activeDraft}
                columnId={activeChild.columnId}
                filterType={activeChild.filterType}
                allOptions={allOptions}
                onChange={(r) => setDrafts((prev) => ({ ...prev, [activeChild.columnId]: r }))}
                autoFocusInput={false}
              />
            </div>
          </div>

          <div className="filter-popover__footer editable-cell-popover__footer">
            <Button variant="secondary" size="s" fullWidth onClick={handleClearAll}>
              Очистить всё
            </Button>
            <Button
              variant="primary"
              size="s"
              fullWidth
              onClick={() => {
                for (const c of filterChildren) {
                  onApply(c.columnId, drafts[c.columnId] ?? null);
                }
                requestClose();
              }}
            >
              Применить
            </Button>
          </div>
        </>
      )}
    </FilterPopoverShell>
  );
}
